<template>
  <div class="sug-box">
    <!-- Nói thẳng giới hạn ra TRƯỚC khi coach gõ prompt. Không có dòng này thì họ viết
         "gửi lúc 5h sáng", giờ đó bị loại, và trông như AI không nghe lời. -->
    <div class="sug-scope">
      AI chỉ được xếp trong khung <b>{{ scopeText }}</b>
      — giờ nằm ngoài sẽ bị loại và hệ thống tự rải lại.
    </div>

    <!-- Bấm "Dùng khung giờ này" là AI xếp luôn: kết quả + xác nhận lưu lên trước,
         ô mô tả chỉ để chỉnh lại khi coach muốn -->
    <div v-if="loading && !rows.length" class="sug-loading">AI đang xếp giờ trong khung…</div>

    <template v-if="rows.length">
      <div v-if="shiftDays > 0" class="sug-shift">
        Giờ gửi đã trôi qua nên cả cụm được dời lên <b>{{ shiftDays }} ngày</b>,
        giữ nguyên khoảng cách giữa các mốc.
      </div>

      <div class="sug-list">
        <div v-for="row in rows" :key="`sgr-${row.id}`" class="sug-row">
          <span class="sug-when">{{ row.oldLabel }}</span>
          <span class="sug-name" :title="row.name">{{ row.name }}</span>
          <span class="sug-why" :title="row.reason">{{ row.reason }}</span>
          <span class="sug-to" :class="row.fallback ? 'is-auto' : 'is-ai'">{{ row.newLabel }}</span>
        </div>
      </div>

      <div class="sug-actions">
        <b-button variant="success" size="sm" :disabled="busy || loading" @click="$emit('apply')">
          Lưu giờ mới cho {{ rows.length }} nhắc nhở
        </b-button>
        <span class="text-muted font-size-sm">Chưa ưng thì mô tả nhịp sinh hoạt bên dưới để AI xếp lại.</span>
      </div>
    </template>

    <label class="sug-label" :class="{ 'mt-4': rows.length }">
      {{ rows.length ? 'Chỉnh lại bằng AI (không bắt buộc)' : 'Mô tả nhịp sinh hoạt của khách' }}
    </label>
    <b-form-textarea
      :value="value"
      rows="2"
      max-rows="4"
      placeholder="VD: thuốc uống sau bữa sáng và bữa tối, vận động lúc chiều mát, tránh giờ ngủ trưa"
      @input="$emit('input', $event)"
    />

    <div class="sug-chips">
      <button
        v-for="(sample, i) in samples"
        :key="`sg-${i}`"
        type="button"
        class="sug-chip"
        @click="$emit('input', sample)"
      >
        {{ sample }}
      </button>
    </div>

    <div class="sug-actions">
      <b-button
        :variant="rows.length ? 'outline-success' : 'success'"
        size="sm"
        :disabled="loading || busy"
        @click="$emit('fetch')"
      >
        {{ loading ? 'AI đang xếp…' : (rows.length ? 'Xếp lại theo mô tả' : 'Lấy gợi ý') }}
      </b-button>
      <span v-if="message" class="text-muted font-size-sm">{{ message }}</span>
    </div>
  </div>
</template>

<script>
/**
 * Khối "nhờ AI xếp giờ". Tách riêng để hộp "quá giờ gửi" và hộp "lệch khung giờ"
 * dùng CHUNG một khối, không phải hai bản sao lệch nhau dần theo thời gian — cả hai
 * hộp đang giải đúng một việc: đặt lại giờ cho một nhóm nhắc nhở.
 *
 * Không giữ state, không gọi API. Cha nắm dữ liệu vì chỉ cha mới biết đang xếp cho
 * danh sách nào và có phải dời ngày hay không.
 */
export default {
  name: 'TimeSuggestPanel',

  props: {
    /** Prompt của coach (v-model). */
    value: { type: String, default: '' },
    /** Khung giờ đang áp, dạng "06:00–21:00" hoặc nhiều khung cách nhau dấu phẩy. */
    scopeText: { type: String, default: '' },
    samples: { type: Array, default: () => [] },
    /** [{ id, name, reason, oldLabel, newLabel, fallback }] */
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    message: { type: String, default: '' },
    /** Số ngày cả cụm bị dời tới. 0 = không dời, khối cảnh báo không hiện. */
    shiftDays: { type: Number, default: 0 },
  },
};
</script>

<style scoped>
.sug-box {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
  background: #fff;
}

.sug-scope {
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 4px;
  background: #f1faff;
  color: #3699ff;
  font-size: 12.5px;
}

.sug-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #3f4254;
}

.sug-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.sug-chip {
  padding: 3px 10px;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  background: #f8f9fc;
  color: #7e8299;
  font-size: 11.5px;
  line-height: 18px;
}
.sug-chip:hover {
  border-color: #3699ff;
  color: #3699ff;
}

.sug-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.sug-loading {
  padding: 8px 10px;
  border-radius: 4px;
  background: #f3f6f9;
  color: #7e8299;
  font-size: 12.5px;
}

.sug-shift {
  margin-top: 10px;
  padding: 6px 10px;
  border-radius: 4px;
  background: #fff4de;
  color: #8a6d3b;
  font-size: 12.5px;
}

/* Bảng trước/sau. Style riêng chứ không dùng .aff-* của component cha: scoped style
   không xuyên qua ranh giới component, mượn tên là mất sạch định dạng. */
.sug-list {
  margin-top: 8px;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #ebedf3;
  border-radius: 4px;
  background: #fff;
}
.sug-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-bottom: 1px solid #f3f6f9;
  font-size: 12.5px;
}
.sug-row:last-child {
  border-bottom: none;
}
.sug-when {
  flex: 0 0 96px;
  color: #7e8299;
  font-variant-numeric: tabular-nums;
}
.sug-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sug-why {
  flex: 0 0 160px;
  color: #7e8299;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sug-to {
  flex: 0 0 auto;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.sug-to::before {
  content: '→ ';
  color: #b5b5c3;
  font-weight: 400;
}
.sug-to.is-ai {
  color: #1bc5bd;
}
/* Xám nhạt, không phải đỏ: dòng này vẫn có giờ dùng được, chỉ là do hệ thống rải
   chứ không phải AI chọn. Tô đỏ là coach tưởng hỏng. */
.sug-to.is-auto {
  color: #7e8299;
  font-weight: 400;
}

@media (max-width: 760px) {
  .sug-why {
    display: none;
  }
}
</style>
